import {
  CalendarDateTime,
  toCalendarDate,
  toTime,
} from "@internationalized/date";
import { DayChoice } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/DayChoice";
import { WeekRepeat } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/PeriodicDuration";
import { getInitialTimedDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/initialDurationValues";
import { getDatesFromWeekdays } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/durationInputs/periodicDurationUtils";
import { Rule } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  DateRangePicker,
  Button,
  Field,
  HStack,
  Stack,
  Text,
  TimePicker,
} from "shared/components/Spor";
import { Select } from "shared/components/forms/Select";
import { useTheme } from "styled-components";

type AddTimeIntervalRuleProps = {
  savedRule?: Rule;
  onSaveClick: (rule: Rule) => void;
  onClose: () => void;
};

export const AddTimeIntervalRule: FC<AddTimeIntervalRuleProps> = ({
  savedRule,
  onSaveClick,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [startDateTime, setStartDateTime] = useState<CalendarDateTime>(
    savedRule?.startDateTime || getInitialTimedDuration().from_time,
  );
  const [endDateTime, setEndDateTime] = useState<CalendarDateTime>(
    savedRule?.endDateTime || getInitialTimedDuration().to_time,
  );
  const [days, setDays] = useState<number[]>(savedRule?.days || []);
  const [repeat, setRepeat] = useState<WeekRepeat>(
    savedRule?.repeat || WeekRepeat.WEEKLY,
  );

  const invalidRule =
    getDatesFromWeekdays(
      toCalendarDate(startDateTime),
      toCalendarDate(endDateTime),
      days,
      repeat,
    ).length === 0;

  return (
    <Box
      width="min-content"
      padding="24px"
      boxShadow={`inset 0 0 0 1px ${theme.colorOutlineMain}`}
      borderRadius="sm"
    >
      <Field>
        <Text variant="sm">{t("Hendelsen er aktiv mellom")}</Text>
        <DateRangePicker
          variant="core"
          startLabel="Startdato"
          endLabel="Sluttdato"
          aria-label="Dato"
          value={{
            start: toCalendarDate(startDateTime),
            end: toCalendarDate(endDateTime),
          }}
          onValueChange={(date) => {
            if (date) {
              setStartDateTime((prev) =>
                prev.set({
                  day: date.start?.day,
                  month: date.start?.month,
                  year: date.start?.year,
                }),
              );
              setEndDateTime((prev) =>
                prev.set({
                  day: date.end?.day,
                  month: date.end?.month,
                  year: date.end?.year,
                }),
              );
            }
          }}
        />
      </Field>
      <Text variant="sm" paddingTop="6px">
        {t("I tidsrommet")}
      </Text>
      <HStack gap="6px">
        <TimePicker
          label={t("Fra")}
          minuteInterval={5}
          value={toTime(startDateTime)}
          onValueChange={(time) => {
            setStartDateTime((prev) =>
              prev.set({
                hour: time?.hour,
                minute: time?.minute,
              }),
            );
          }}
          width="100%"
        />
        <TimePicker
          label={t("Til")}
          minuteInterval={5}
          value={toTime(endDateTime)}
          onValueChange={(time) => {
            setEndDateTime((prev) =>
              prev.set({
                hour: time?.hour,
                minute: time?.minute,
              }),
            );
          }}
          width="100%"
        />
      </HStack>
      <DayChoice selectedDays={days} setSelectedDays={setDays} />
      <Select
        label={t("Gjentas")}
        value={repeat}
        onChange={(e) => {
          const value = e.target.value as WeekRepeat;
          if (value === WeekRepeat.WEEKLY || value === WeekRepeat.BIWEEKLY)
            setRepeat(value);
        }}
      >
        {[
          { label: t("Hver uke"), value: WeekRepeat.WEEKLY },
          { label: t("Hver 2. uke"), value: WeekRepeat.BIWEEKLY },
        ].map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
      <Stack flexDirection="row" justifyContent="end" paddingTop="18px">
        <Button variant="tertiary" size="sm" onClick={onClose}>
          {t("Avbryt")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            onSaveClick({
              id: savedRule?.id || Math.random().toString(),
              startDateTime,
              endDateTime,
              days,
              repeat,
            })
          }
          disabled={invalidRule}
        >
          {t("Lagre regel")}
        </Button>
      </Stack>
    </Box>
  );
};
